import store from "@/store";
import dayjs from "dayjs";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { DecryptAes, EncryptAes } from "@/utils/cryptohelper";
import { md5Encrypt } from "@/utils";
import { ApiEncryptService } from "./apiencryp.service";
import { loginService } from "@/services/login.service";
import { Location } from "@/models/requests/location.interface";
import { LogRequest } from "@/models/requests/user.interface";
import { ODataMultipleResponse, ODataBaseResponse } from "@/models/responses/odata.response.interface";
import { AesResponse } from "@/models/responses/aes.response.interface";
import { PasswordResponse } from "@/models/responses/user.response.interface";

interface EnviromentVariableResponse extends ODataBaseResponse {
  defaultvalue: string;
}

interface NewPasswordReqResponse extends ODataBaseResponse {
  rs_solicitudcontraseniaid: string;
}

interface GetPasswordReqResponse extends ODataBaseResponse {
  rs_solicitudcontraseniaid: string;
  statecode: number;
  _rs_usuarioportalcallcenterid_value: string;
  rs_usuarioportalcallcenterid: {
    rs_telefono: string;
  };
}

interface FlowResponse {
  code: number;
  message: string;
}

interface CreateLoginFlowResponse {
  code: number;
  message: string;
  data: {
    token: string;
    user: {
      id: string;
      name: string;
    };
    systemUserId: string;
    sessionId: string;
    requestCode: string;
  };
}

interface GetUserFlowResponse {
  code: number;
  message: string;
  data: {
    userId: string;
    phone: string;
  };
}

interface DecryptResponse {
  plainText: string;
}

class UserService extends ApiEncryptService {
  public constructor(config?: AxiosRequestConfig) {
    super(config);
  }

  /**
   * Gets the value of enviorenment variable
   * @param {string} name
   * @returns {Promise<string>}
   */
  public async getEnviromentVariable(name: string): Promise<string> {
    await this.getToken();

    const resAes = await this.get<AesResponse, AxiosResponse<AesResponse>>(`d365api-login/v1/environmentvariabledefinitions?` + EncryptAes(`$select=defaultvalue&$filter=schemaname eq '${name}'`), {
      headers: {
        "Ocp-Apim-Subscription-Key": store.getters.getSubscription,
        Authorization: "Bearer " + store.getters.getToken
      }
    });

    const resDecrypt = DecryptAes(resAes.data.ValueResponse) as string;
    const json = JSON.parse(resDecrypt);
    const response = json as ODataMultipleResponse<EnviromentVariableResponse[]>;

    return response?.value[0]?.defaultvalue;
  }

  /**
   * Get record of rs_usuarioportalcallcenter by email
   * @param {string} email
   * @returns {Promise<GetUserFlowResponse>}
   */
  public async getUserCCByEmail(user: string): Promise<GetUserFlowResponse> {
    await this.getToken();

    const resAes = await this.post<AesResponse, unknown, AxiosResponse<AesResponse>>(
      `flows-login/v1/workflows/${store.getters.getFlowValidateUser}/triggers/manual/paths/invoke`,
      // `flows-login/v1/workflows/3423ed990fef415780c29113200740a6/triggers/manual/paths/invoke`,
      {
        ValueRequest: EncryptAes(
          JSON.stringify({
            user: user
          })
        )
      },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": store.getters.getSubscription,
          Authorization: "Bearer " + store.getters.getToken
        }
      }
    );

    const resDecrypt = DecryptAes(resAes.data.ValueResponse) as string;
    const json = JSON.parse(resDecrypt);
    const response = json as GetUserFlowResponse;
    return response;
  }

  /**
   * Get record of PasswordRequest by code
   * @param {string} code
   * @returns {Promise<PasswordResponse>}
   */
  public async getPasswordRequestByCode(code: string): Promise<PasswordResponse> {
    await this.getToken();

    const resAes = await this.get<AesResponse, AxiosResponse<AesResponse>>(
      `d365api-login/v1/rs_solicitudcontrasenias?` +
        EncryptAes(
          `$select=rs_solicitudcontraseniaid,_rs_usuarioportalcallcenterid_value,statecode&$expand=rs_usuarioportalcallcenterid($select=rs_telefono)&$filter=rs_codigo eq '${encodeURIComponent(code)}'`
        ),
      {
        headers: {
          "Ocp-Apim-Subscription-Key": store.getters.getSubscription,
          Authorization: "Bearer " + store.getters.getToken
        }
      }
    );

    const resDecrypt = DecryptAes(resAes.data.ValueResponse) as string;
    const json = JSON.parse(resDecrypt);
    const response = json as ODataMultipleResponse<GetPasswordReqResponse[]>;

    if (response?.value != null && response?.value?.length > 0) {
      return {
        requestId: response.value[0].rs_solicitudcontraseniaid,
        state: response.value[0].statecode,
        userId: response.value[0]._rs_usuarioportalcallcenterid_value,
        phone: response.value[0].rs_usuarioportalcallcenterid?.rs_telefono ? response.value[0].rs_usuarioportalcallcenterid?.rs_telefono : ""
      };
    } else {
      return null;
    }
  }

  /**
   * Create a PasswordRequest
   *
   * @param {string} userId
   * @param {Location} location
   * @returns {Promise<string>}
   */
  public async createPasswordRequest(userId: string, location: Location): Promise<string> {
    await this.getToken();

    const resAes = await this.post<AesResponse, unknown, AxiosResponse<AesResponse>>(
      "d365api-login/v1/rs_solicitudcontrasenias?" + EncryptAes("$select=rs_solicitudcontraseniaid"),
      {
        ValueRequest: EncryptAes(
          JSON.stringify({
            rs_name: "Solicitud portal Call Center",
            "rs_usuarioportalcallcenterid@odata.bind": `/rs_usuarioportalcallcenters(${userId})`,
            rs_ip: location.ip,
            rs_latitud: location.latitud,
            rs_longitud: location.longitud,
            rs_explorador: location.browser
          })
        )
      },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": store.getters.getSubscription,
          Authorization: "Bearer " + store.getters.getToken
        }
      }
    );

    const resDecrypt = DecryptAes(resAes.data.ValueResponse) as string;
    const json = JSON.parse(resDecrypt);
    const response = json as NewPasswordReqResponse;

    return response.rs_solicitudcontraseniaid;
  }

  /**
   * Get active record of Login
   * @param {string} token
   * @returns {Promise<CreateLoginFlowResponse>}
   */
  public async validateActiveLogin(token: string, user: string): Promise<CreateLoginFlowResponse> {
    await this.getToken();
    const resAes = await this.post<AesResponse, unknown, AxiosResponse<AesResponse>>(
      `flows-login/v1/workflows/${store.getters.getFlowValidateLogin}/triggers/manual/paths/invoke`,
      // `flows-login/v1/workflows/9a70441d59b640ffb73a442ebf6bc7b0/triggers/manual/paths/invoke`,
      {
        ValueRequest: EncryptAes(
          JSON.stringify({
            token: token,
            userId: user
          })
        )
      },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": store.getters.getSubscription,
          Authorization: "Bearer " + store.getters.getToken
        }
      }
    );

    const resDecrypt = DecryptAes(resAes.data.ValueResponse) as string;
    const json = JSON.parse(resDecrypt);
    const response = json as CreateLoginFlowResponse;
    return response;
  }

  /**
   * Create a Login record
   *
   * @param {LogRequest} credential
   * @param {Location} location
   * @returns {Promise<CreateLoginFlowResponse>}
   */
  public async createLogin(credential: LogRequest, location: Location): Promise<CreateLoginFlowResponse> {
    await this.getToken();
    const resAes = await this.post<AesResponse, unknown, AxiosResponse<AesResponse>>(
      `flows-login/v1/workflows/${store.getters.getFlowCreateLogin}/triggers/manual/paths/invoke`,
      // `flows-login/v1/workflows/13c44cbb8b9c4f36929292b8af5b3b6d/triggers/manual/paths/invoke`,
      {
        ValueRequest: EncryptAes(
          JSON.stringify({
            user: credential.user,
            password: md5Encrypt(credential.password),
            ip: location.ip,
            latitude: location.latitud,
            longitude: location.longitud
          })
        )
      },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": store.getters.getSubscription,
          Authorization: "Bearer " + store.getters.getToken
        }
      }
    );

    const resDecrypt = DecryptAes(resAes.data.ValueResponse) as string;
    const json = JSON.parse(resDecrypt);
    const response = json as CreateLoginFlowResponse;
    return response;
  }

  /**
   * Create a Login record
   *
   * @param {LogRequest} credential
   * @param {Location} location
   * @returns {Promise<FlowResponse>}
   */
  public async logOut(): Promise<FlowResponse> {
    await this.getToken();
    const resAes = await this.post<AesResponse, unknown, AxiosResponse<AesResponse>>(
      `flows-login/v1/workflows/${store.getters.getFlowLogout}/triggers/manual/paths/invoke`,
      // `flows-login/v1/workflows/11c4fd46dc0b4b629fb19a65f29f84c3/triggers/manual/paths/invoke`,
      {
        ValueRequest: EncryptAes(
          JSON.stringify({
            sessionId: store.getters.getSession
          })
        )
      },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": store.getters.getSubscription,
          Authorization: "Bearer " + store.getters.getToken
        }
      }
    );

    const resDecrypt = DecryptAes(resAes.data.ValueResponse) as string;
    const json = JSON.parse(resDecrypt);
    const response = json as FlowResponse;
    return response;
  }

  /**
   * Flow for send an email for change password
   *
   * @param {string} userId
   * @param {string} requestId
   * @returns {Promise<FlowResponse>}
   */
  public async sendEmailChangePassword(userId: string, requestId: string): Promise<FlowResponse> {
    await this.getToken();

    const resAes = await this.post<AesResponse, unknown, AxiosResponse<AesResponse>>(
      `flows-login/v1/workflows/${store.getters.getFlowChangePasswordRequest}/triggers/manual/paths/invoke`,
      // `flows-login/v1/workflows/4fb0ec5af948401aaec71f8cc8b47127/triggers/manual/paths/invoke`,
      {
        ValueRequest: EncryptAes(
          JSON.stringify({
            userId: userId,
            requestId: requestId
          })
        )
      },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": store.getters.getSubscription,
          Authorization: "Bearer " + store.getters.getToken
        }
      }
    );

    const resDecrypt = DecryptAes(resAes.data.ValueResponse) as string;
    const json = JSON.parse(resDecrypt);
    const response = json as FlowResponse;
    return response;
  }

  /**
   * Flow for send an email for change password
   *
   * @param {string} userId
   * @param {string} password
   * @returns {Promise<FlowResponse>}
   */
  public async changePassword(userId: string, password: string, requestId: string, location: Location): Promise<FlowResponse> {
    await this.getToken();

    const resAes = await this.post<AesResponse, unknown, AxiosResponse<AesResponse>>(
      `flows-login/v1/workflows/${store.getters.getFlowChangePassword}/triggers/manual/paths/invoke`,
      // `flows-login/v1/workflows/97034a208769456db10e4976ce7b1b7b/triggers/manual/paths/invoke`,
      {
        ValueRequest: EncryptAes(
          JSON.stringify({
            userId: userId,
            newPassword: md5Encrypt(password),
            requestId: requestId,
            ip: location.ip,
            latitude: location.latitud,
            longitude: location.longitud,
            browser: location.browser
          })
        )
      },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": store.getters.getSubscription,
          Authorization: "Bearer " + store.getters.getToken
        }
      }
    );

    const resDecrypt = DecryptAes(resAes.data.ValueResponse) as string;
    const json = JSON.parse(resDecrypt);
    const response = json as FlowResponse;
    return response;
  }

  /**
   * Decrypts the cipher text.
   *
   * @param {string} cipherText
   * @returns {Promise<string>}
   */
  public async decrypt(cipherText: string): Promise<string> {
    await this.getToken();

    const resAes = await this.post<AesResponse, unknown, AxiosResponse<AesResponse>>(
      "Generics/version1/crypto/decrypt",
      {
        ValueRequest: EncryptAes(
          JSON.stringify({
            cipherText: cipherText
          })
        )
      },
      {
        headers: {
          "Ocp-Apim-Subscription-Key": store.getters.getSubscription,
          Authorization: "Bearer " + store.getters.getToken
        }
      }
    );

    const resDecrypt = DecryptAes(resAes.data.ValueResponse) as string;
    const json = JSON.parse(resDecrypt);
    const response = json as DecryptResponse;
    return response.plainText;
  }

  async getToken(): Promise<void> {
    if (!this.baseUri) {
      this.baseUri = store.getters.getUrlBase;
    }
    const token = store.getters.getToken;
    if (!token) {
      await loginService.getTokenApim();
    } else {
      const expire = store.getters.getTokenExpire;
      if (dayjs().isAfter(expire)) {
        await loginService.getTokenApim();
      }
    }
  }
}

export const userService = new UserService();
